/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState } from 'react';

type LanguageCode = 'en' | 'hi' | 'bn' | 'te' | 'mr' | 'ta' | 'ur';

const LANGUAGE_OPTIONS: { code: LanguageCode; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'mr', label: 'मराठी' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'ur', label: 'اردو' },
];

const translations: Record<LanguageCode, Record<string, string>> = {
  en: {
    'nav.dashboard': 'Dashboard',
    'nav.quick-capture': 'Quick Capture',
    'nav.profile': 'Politician Profile',
    'nav.constituency': 'Constituency',
    'nav.grievances': 'Grievances',
    'nav.appointments': 'Appointments',
    'nav.events': 'Events',
    'nav.voters': 'Voter Database',
    'nav.polls': 'Polls & Surveys',
    'nav.booths': 'Booth Management',
    'nav.legislative': 'Legislative',
    'nav.citizen': 'Citizen Engagement',
    'nav.darshan': 'Tirupati Darshan',
    'nav.darshans': 'Darshans',
    'nav.parliamentary': 'Parliamentary',
    'nav.ai-studio': 'AI Studio',
    'nav.omniscan': 'OmniScan',
    'nav.morning-brief': 'Morning Brief',
    'nav.sentiment': 'Sentiment Dashboard',
    'nav.opposition': 'Opposition Tracker',
    'nav.voice-intelligence': 'Voice Intelligence',
    'nav.promises': 'Promises Tracker',
    'nav.content-factory': 'Content Factory',
    'nav.whatsapp-intelligence': 'WhatsApp Intel',
    'nav.smart-visit': 'Smart Visit Planner',
    'nav.briefing': 'Political Briefings',
    'nav.predictive-crisis': 'Predictive Crisis',
    'nav.agent-system': 'Agent System',
    'nav.deepfake-shield': 'Deepfake Shield',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'Relationship Graph',
    'nav.economic-intelligence': 'Economic Intelligence',
    'nav.citizen-services': 'Citizen Services',
    'nav.election-command': 'Election Command',
    'nav.finance-compliance': 'Finance Compliance',
    'nav.party-integration': 'Party Integration',
    'nav.digital-twin': 'Digital Twin',
    'nav.projects': 'Dev Projects',
    'nav.media': 'Media Monitor',
    'nav.communication': 'Communication',
    'nav.finance': 'Finance',
    'nav.team': 'Team',
    'nav.analytics': 'Analytics',
    'nav.documents': 'Documents',
    'nav.settings': 'Settings',
    'nav.staff-management': 'Staff Management',
    'nav.superadmin': 'Platform Admin',
    'nav.website-admin': 'Website CMS',
    'group.core': 'CORE',
    'group.political': 'POLITICAL',
    'group.services': 'SERVICES',
    'group.intelligence': 'INTELLIGENCE',
    'group.future': 'FUTURE LAB',
    'group.admin': 'ADMINISTRATION',
    'header.search': 'Search...',
    'header.notifications': 'Notifications',
    'header.profile': 'Profile',
    'header.settings': 'Settings',
    'header.logout': 'Sign out',
  },
  hi: {
    'nav.dashboard': 'डैशबोर्ड',
    'nav.quick-capture': 'क्विक कैप्चर',
    'nav.profile': 'नेता प्रोफ़ाइल',
    'nav.constituency': 'निर्वाचन क्षेत्र',
    'nav.grievances': 'शिकायतें',
    'nav.appointments': 'अपॉइंटमेंट',
    'nav.events': 'कार्यक्रम',
    'nav.voters': 'मतदाता डेटाबेस',
    'nav.polls': 'सर्वेक्षण',
    'nav.booths': 'बूथ प्रबंधन',
    'nav.legislative': 'विधान कार्य',
    'nav.citizen': 'जन सहभागिता',
    'nav.darshan': 'तिरुपति दर्शन',
    'nav.darshans': 'दर्शन',
    'nav.parliamentary': 'संसदीय',
    'nav.ai-studio': 'एआई स्टूडियो',
    'nav.omniscan': 'ओम्नीस्कैन',
    'nav.morning-brief': 'मॉर्निंग ब्रीफ़',
    'nav.sentiment': 'भावना डैशबोर्ड',
    'nav.opposition': 'विपक्ष ट्रैकर',
    'nav.voice-intelligence': 'वॉइस इंटेलिजेंस',
    'nav.promises': 'वादे ट्रैकर',
    'nav.content-factory': 'कंटेंट फैक्टरी',
    'nav.whatsapp-intelligence': 'व्हाट्सऐप इंटेल',
    'nav.smart-visit': 'स्मार्ट विज़िट',
    'nav.briefing': 'राजनीतिक ब्रीफ़िंग',
    'nav.predictive-crisis': 'पूर्वानुमान संकट',
    'nav.agent-system': 'एजेंट सिस्टम',
    'nav.deepfake-shield': 'डीपफेक शील्ड',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'रिलेशनशिप ग्राफ',
    'nav.economic-intelligence': 'आर्थिक इंटेलिजेंस',
    'nav.citizen-services': 'नागरिक सेवाएँ',
    'nav.election-command': 'चुनाव कमांड',
    'nav.finance-compliance': 'वित्तीय अनुपालन',
    'nav.party-integration': 'पार्टी इंटीग्रेशन',
    'nav.digital-twin': 'डिजिटल ट्विन',
    'nav.projects': 'डेव प्रोजेक्ट्स',
    'nav.media': 'मीडिया मॉनिटर',
    'nav.communication': 'संचार',
    'nav.finance': 'वित्त',
    'nav.team': 'टीम',
    'nav.analytics': 'एनालिटिक्स',
    'nav.documents': 'दस्तावेज़',
    'nav.settings': 'सेटिंग्स',
    'nav.staff-management': 'स्टाफ मैनेजमेंट',
    'nav.superadmin': 'प्लेटफ़ॉर्म एडमिन',
    'group.core': 'कोर',
    'group.political': 'राजनीतिक',
    'group.services': 'सेवाएँ',
    'group.intelligence': 'इंटेलिजेंस',
    'group.future': 'भविष्य लैब',
    'group.admin': 'प्रशासन',
    'header.search': 'खोजें...',
    'header.notifications': 'सूचनाएँ',
    'header.profile': 'प्रोफ़ाइल',
    'header.settings': 'सेटिंग्स',
    'header.logout': 'साइन आउट',
  },
  bn: {
    'nav.dashboard': 'ড্যাশবোর্ড',
    'nav.quick-capture': 'কুইক ক্যাপচার',
    'nav.profile': 'রাজনীতিবিদ প্রোফাইল',
    'nav.constituency': 'নির্বাচনী এলাকা',
    'nav.grievances': 'অভিযোগ',
    'nav.appointments': 'অ্যাপয়েন্টমেন্ট',
    'nav.events': 'ইভেন্ট',
    'nav.voters': 'ভোটার ডেটাবেস',
    'nav.polls': 'জরিপ',
    'nav.booths': 'বুথ ব্যবস্থাপনা',
    'nav.legislative': 'বিধান',
    'nav.citizen': 'নাগরিক সম্পৃক্ততা',
    'nav.darshan': 'তিরুপতি দর্শন',
    'nav.darshans': 'দর্শন',
    'nav.parliamentary': 'সাংসদীয়',
    'nav.ai-studio': 'এআই স্টুডিও',
    'nav.omniscan': 'ওমনিস্ক্যান',
    'nav.morning-brief': 'মর্নিং ব্রিফ',
    'nav.sentiment': 'সেন্টিমেন্ট ড্যাশবোর্ড',
    'nav.opposition': 'বিরোধী ট্র্যাকার',
    'nav.voice-intelligence': 'ভয়েস ইন্টেলিজেন্স',
    'nav.promises': 'প্রতিশ্রুতি ট্র্যাকার',
    'nav.content-factory': 'কনটেন্ট ফ্যাক্টরি',
    'nav.whatsapp-intelligence': 'হোয়াটসঅ্যাপ ইন্টেল',
    'nav.smart-visit': 'স্মার্ট ভিজিট',
    'nav.briefing': 'রাজনৈতিক ব্রিফিং',
    'nav.predictive-crisis': 'পূর্বাভাস সংকট',
    'nav.agent-system': 'এজেন্ট সিস্টেম',
    'nav.deepfake-shield': 'ডিপফেক শিল্ড',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'সম্পর্ক গ্রাফ',
    'nav.economic-intelligence': 'অর্থনৈতিক ইন্টেলিজেন্স',
    'nav.citizen-services': 'নাগরিক সেবা',
    'nav.election-command': 'নির্বাচন কমান্ড',
    'nav.finance-compliance': 'ফাইন্যান্স কমপ্লায়েন্স',
    'nav.party-integration': 'পার্টি ইন্টিগ্রেশন',
    'nav.digital-twin': 'ডিজিটাল টুইন',
    'nav.projects': 'ডেভ প্রজেক্ট',
    'nav.media': 'মিডিয়া মনিটর',
    'nav.communication': 'কমিউনিকেশন',
    'nav.finance': 'ফাইন্যান্স',
    'nav.team': 'টিম',
    'nav.analytics': 'অ্যানালিটিক্স',
    'nav.documents': 'ডকুমেন্টস',
    'nav.settings': 'সেটিংস',
    'nav.staff-management': 'স্টাফ ম্যানেজমেন্ট',
    'nav.superadmin': 'প্ল্যাটফর্ম অ্যাডমিন',
    'group.core': 'মূল',
    'group.political': 'রাজনৈতিক',
    'group.services': 'সেবা',
    'group.intelligence': 'ইন্টেলিজেন্স',
    'group.future': 'ফিউচার ল্যাব',
    'group.admin': 'অ্যাডমিন',
    'header.search': 'অনুসন্ধান...',
    'header.notifications': 'বিজ্ঞপ্তি',
    'header.profile': 'প্রোফাইল',
    'header.settings': 'সেটিংস',
    'header.logout': 'সাইন আউট',
  },
  te: {
    'nav.dashboard': 'డాష్‌బోర్డ్',
    'nav.quick-capture': 'క్విక్ క్యాప్చర్',
    'nav.profile': 'నేత ప్రొఫైల్',
    'nav.constituency': 'నియోజకవర్గం',
    'nav.grievances': 'ఫిర్యాదులు',
    'nav.appointments': 'అపాయింట్‌మెంట్లు',
    'nav.events': 'ఈవెంట్లు',
    'nav.voters': 'వోటర్ డేటాబేస్',
    'nav.polls': 'సర్వేలు',
    'nav.booths': 'బూత్ నిర్వహణ',
    'nav.legislative': 'శాసన కార్యం',
    'nav.citizen': 'పౌర భాగస్వామ్యం',
    'nav.darshan': 'తిరుపతి దర్శనం',
    'nav.darshans': 'దర్శనాలు',
    'nav.parliamentary': 'పార్లమెంటరీ',
    'nav.ai-studio': 'ఎఐ స్టూడియో',
    'nav.omniscan': 'ఒమ్నిస్కాన్',
    'nav.morning-brief': 'మార్నింగ్ బ్రీఫ్',
    'nav.sentiment': 'సెంటిమెంట్ డాష్‌బోర్డ్',
    'nav.opposition': 'ప్రతిపక్ష ట్రాకర్',
    'nav.voice-intelligence': 'వాయిస్ ఇంటెలిజెన్స్',
    'nav.promises': 'హామీలు ట్రాకర్',
    'nav.content-factory': 'కంటెంట్ ఫ్యాక్టరీ',
    'nav.whatsapp-intelligence': 'వాట్సాప్ ఇంటెల్',
    'nav.smart-visit': 'స్మార్ట్ విజిట్',
    'nav.briefing': 'పోలిటికల్ బ్రీఫింగ్స్',
    'nav.predictive-crisis': 'ముందస్తు సంక్షోభం',
    'nav.agent-system': 'ఏజెంట్ సిస్టమ్',
    'nav.deepfake-shield': 'డీప్‌ఫేక్ షీల్డ్',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'సంబంధ గ్రాఫ్',
    'nav.economic-intelligence': 'ఆర్థిక ఇంటెలిజెన్స్',
    'nav.citizen-services': 'పౌర సేవలు',
    'nav.election-command': 'ఎన్నికల కమాండ్',
    'nav.finance-compliance': 'ఫైనాన్స్ కంప్లయన్స్',
    'nav.party-integration': 'పార్టీ ఇంటిగ్రేషన్',
    'nav.digital-twin': 'డిజిటల్ ట్విన్',
    'nav.projects': 'డెవ్ ప్రాజెక్ట్స్',
    'nav.media': 'మీడియా మానిటర్',
    'nav.communication': 'కమ్యూనికేషన్',
    'nav.finance': 'ఫైనాన్స్',
    'nav.team': 'టీమ్',
    'nav.analytics': 'అనలిటిక్స్',
    'nav.documents': 'డాక్యుమెంట్స్',
    'nav.settings': 'సెట్టింగ్స్',
    'nav.staff-management': 'స్టాఫ్ మేనేజ్‌మెంట్',
    'nav.superadmin': 'ప్లాట్‌ఫాం అడ్మిన్',
    'group.core': 'కోర్',
    'group.political': 'రాజకీయ',
    'group.services': 'సేవలు',
    'group.intelligence': 'ఇంటెలిజెన్స్',
    'group.future': 'ఫ్యూచర్ ల్యాబ్',
    'group.admin': 'అడ్మిన్',
    'header.search': 'శోధించండి...',
    'header.notifications': 'నోటిఫికేషన్లు',
    'header.profile': 'ప్రొఫైల్',
    'header.settings': 'సెట్టింగ్స్',
    'header.logout': 'సైన్ అవుట్',
  },
  mr: {
    'nav.dashboard': 'डॅशबोर्ड',
    'nav.quick-capture': 'क्विक कॅप्चर',
    'nav.profile': 'नेता प्रोफाइल',
    'nav.constituency': 'मतदारसंघ',
    'nav.grievances': 'तक्रारी',
    'nav.appointments': 'अपॉइंटमेंट',
    'nav.events': 'कार्यक्रम',
    'nav.voters': 'मतदार डेटाबेस',
    'nav.polls': 'सर्वे',
    'nav.booths': 'बूथ व्यवस्थापन',
    'nav.legislative': 'विधायी',
    'nav.citizen': 'नागरिक सहभाग',
    'nav.darshan': 'तिरुपती दर्शन',
    'nav.darshans': 'दर्शन',
    'nav.parliamentary': 'संसदीय',
    'nav.ai-studio': 'एआय स्टुडिओ',
    'nav.omniscan': 'ओम्निस्कॅन',
    'nav.morning-brief': 'मॉर्निंग ब्रीफ',
    'nav.sentiment': 'भावना डॅशबोर्ड',
    'nav.opposition': 'विरोधी ट्रॅकर',
    'nav.voice-intelligence': 'व्हॉइस इंटेलिजन्स',
    'nav.promises': 'वचने ट्रॅकर',
    'nav.content-factory': 'कंटेंट फॅक्टरी',
    'nav.whatsapp-intelligence': 'व्हॉट्सअॅप इंटेल',
    'nav.smart-visit': 'स्मार्ट भेट',
    'nav.briefing': 'राजकीय ब्रीफिंग',
    'nav.predictive-crisis': 'भविष्यवाणी संकट',
    'nav.agent-system': 'एजंट सिस्टम',
    'nav.deepfake-shield': 'डीपफेक शील्ड',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'नातेसंबंध ग्राफ',
    'nav.economic-intelligence': 'आर्थिक इंटेलिजन्स',
    'nav.citizen-services': 'नागरिक सेवा',
    'nav.election-command': 'निवडणूक कमांड',
    'nav.finance-compliance': 'फायनान्स कंप्लायन्स',
    'nav.party-integration': 'पक्ष समाकलन',
    'nav.digital-twin': 'डिजिटल ट्विन',
    'nav.projects': 'डेव्ह प्रोजेक्ट्स',
    'nav.media': 'मीडिया मॉनिटर',
    'nav.communication': 'कम्युनिकेशन',
    'nav.finance': 'फायनान्स',
    'nav.team': 'टीम',
    'nav.analytics': 'अनालिटिक्स',
    'nav.documents': 'डॉक्युमेंट्स',
    'nav.settings': 'सेटिंग्स',
    'nav.staff-management': 'स्टाफ मॅनेजमेंट',
    'nav.superadmin': 'प्लॅटफॉर्म अॅडमिन',
    'group.core': 'कोर',
    'group.political': 'राजकीय',
    'group.services': 'सेवा',
    'group.intelligence': 'इंटेलिजन्स',
    'group.future': 'फ्युचर लॅब',
    'group.admin': 'प्रशासन',
    'header.search': 'शोधा...',
    'header.notifications': 'सूचना',
    'header.profile': 'प्रोफाइल',
    'header.settings': 'सेटिंग्स',
    'header.logout': 'साइन आऊट',
  },
  ta: {
    'nav.dashboard': 'டாஷ்போர்ட்',
    'nav.quick-capture': 'க்விக் கேப்சர்',
    'nav.profile': 'அரசியல்வாதி சுயவிவரம்',
    'nav.constituency': 'தொகுதி',
    'nav.grievances': 'புகார்கள்',
    'nav.appointments': 'நியமனங்கள்',
    'nav.events': 'நிகழ்வுகள்',
    'nav.voters': 'வாக்காளர் தரவுத்தளம்',
    'nav.polls': 'கருத்துக்கணிப்புகள்',
    'nav.booths': 'பூத் மேலாண்மை',
    'nav.legislative': 'சட்டமன்றம்',
    'nav.citizen': 'மக்கள் ஈடுபாடு',
    'nav.darshan': 'திருப்பதி தரிசனம்',
    'nav.darshans': 'தரிசனங்கள்',
    'nav.parliamentary': 'பாராளுமன்றம்',
    'nav.ai-studio': 'ஏஐ ஸ்டூடியோ',
    'nav.omniscan': 'ஒம்னிஸ்கேன்',
    'nav.morning-brief': 'காலை அறிக்கை',
    'nav.sentiment': 'உணர்வு டாஷ்போர்ட்',
    'nav.opposition': 'எதிர்க்கட்சித் தடம்',
    'nav.voice-intelligence': 'குரல் நுண்ணறிவு',
    'nav.promises': 'வாக்குறுதி தடம்',
    'nav.content-factory': 'உள்ளடக்க தொழிற்சாலை',
    'nav.whatsapp-intelligence': 'வாட்ஸ்அப் நுண்ணறிவு',
    'nav.smart-visit': 'ஸ்மார்ட் விஜிட்',
    'nav.briefing': 'அரசியல் அறிக்கைகள்',
    'nav.predictive-crisis': 'முன்கணிப்பு நெருக்கடி',
    'nav.agent-system': 'ஏஜென்ட் அமைப்பு',
    'nav.deepfake-shield': 'டீப்‌ஃபேக் பாதுகாப்பு',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'உறவு வரைபடம்',
    'nav.economic-intelligence': 'பொருளாதார நுண்ணறிவு',
    'nav.citizen-services': 'குடிமக்கள் சேவை',
    'nav.election-command': 'தேர்தல் கட்டளை',
    'nav.finance-compliance': 'நிதி இணக்கம்',
    'nav.party-integration': 'கட்சித் இணைப்பு',
    'nav.digital-twin': 'டிஜிட்டல் ட்வின்',
    'nav.projects': 'டெவ் திட்டங்கள்',
    'nav.media': 'மீடியா கண்காணிப்பு',
    'nav.communication': 'தொடர்பு',
    'nav.finance': 'நிதி',
    'nav.team': 'குழு',
    'nav.analytics': 'அனலிடிக்ஸ்',
    'nav.documents': 'ஆவணங்கள்',
    'nav.settings': 'அமைப்புகள்',
    'nav.staff-management': 'பணியாளர் மேலாண்மை',
    'nav.superadmin': 'பிளாட்ஃபாரம் நிர்வாகம்',
    'group.core': 'மூலம்',
    'group.political': 'அரசியல்',
    'group.services': 'சேவைகள்',
    'group.intelligence': 'நுண்ணறிவு',
    'group.future': 'எதிர்கால ஆய்வகம்',
    'group.admin': 'நிர்வாகம்',
    'header.search': 'தேடு...',
    'header.notifications': 'அறிவிப்புகள்',
    'header.profile': 'சுயவிவரம்',
    'header.settings': 'அமைப்புகள்',
    'header.logout': 'வெளியேறு',
  },
  ur: {
    'nav.dashboard': 'ڈیش بورڈ',
    'nav.quick-capture': 'کوئیک کیپچر',
    'nav.profile': 'سیاسی پروفائل',
    'nav.constituency': 'حلقہ انتخاب',
    'nav.grievances': 'شکایات',
    'nav.appointments': 'اپائنٹمنٹس',
    'nav.events': 'تقریبات',
    'nav.voters': 'ووٹر ڈیٹا بیس',
    'nav.polls': 'سروے',
    'nav.booths': 'بوتھ مینجمنٹ',
    'nav.legislative': 'قانون سازی',
    'nav.citizen': 'شہری شمولیت',
    'nav.darshan': 'تروپتی درشن',
    'nav.darshans': 'درشنز',
    'nav.parliamentary': 'پارلیمانی',
    'nav.ai-studio': 'اے آئی اسٹوڈیو',
    'nav.omniscan': 'اومنی اسکین',
    'nav.morning-brief': 'مارننگ بریف',
    'nav.sentiment': 'سینٹیمنٹ ڈیش بورڈ',
    'nav.opposition': 'اپوزیشن ٹریکر',
    'nav.voice-intelligence': 'وائس انٹیلیجنس',
    'nav.promises': 'وعدے ٹریکر',
    'nav.content-factory': 'مواد فیکٹری',
    'nav.whatsapp-intelligence': 'واٹس ایپ انٹیل',
    'nav.smart-visit': 'سمارٹ وزٹ',
    'nav.briefing': 'سیاسی بریفنگ',
    'nav.predictive-crisis': 'پیش گوئی بحران',
    'nav.agent-system': 'ایجنٹ سسٹم',
    'nav.deepfake-shield': 'ڈیپ فیک شیلڈ',
    'nav.coalition-forecast': 'Coalition Forecast',
    'nav.crisis-war-room': 'Crisis War Room',
    'nav.relationship-graph': 'رشتہ گراف',
    'nav.economic-intelligence': 'معاشی انٹیلیجنس',
    'nav.citizen-services': 'شہری خدمات',
    'nav.election-command': 'انتخابی کمانڈ',
    'nav.finance-compliance': 'مالیاتی کمپلائنس',
    'nav.party-integration': 'پارٹی انضمام',
    'nav.digital-twin': 'ڈیجیٹل ٹوئن',
    'nav.projects': 'ڈیولپمنٹ پروجیکٹس',
    'nav.media': 'میڈیا مانیٹر',
    'nav.communication': 'کمیونیکیشن',
    'nav.finance': 'فنانس',
    'nav.team': 'ٹیم',
    'nav.analytics': 'اینالٹکس',
    'nav.documents': 'دستاویزات',
    'nav.settings': 'سیٹنگز',
    'nav.staff-management': 'اسٹاف مینجمنٹ',
    'nav.superadmin': 'پلیٹ فارم ایڈمن',
    'group.core': 'کور',
    'group.political': 'سیاسی',
    'group.services': 'خدمات',
    'group.intelligence': 'انٹیلیجنس',
    'group.future': 'مستقبل لیب',
    'group.admin': 'انتظامیہ',
    'header.search': 'تلاش کریں...',
    'header.notifications': 'اطلاعات',
    'header.profile': 'پروفائل',
    'header.settings': 'سیٹنگز',
    'header.logout': 'سائن آؤٹ',
  },
};

interface I18nContextValue {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  t: (key: string) => string;
  languages: typeof LANGUAGE_OPTIONS;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<LanguageCode>(() => {
    const stored = localStorage.getItem('nethra_language') as LanguageCode | null;
    return stored || 'en';
  });

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage: (code) => {
      setLanguage(code);
      localStorage.setItem('nethra_language', code);
    },
    t: (key) => translations[language]?.[key] || translations.en[key] || key,
    languages: LANGUAGE_OPTIONS,
  }), [language]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within LanguageProvider');
  return ctx;
}
